import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormattedNumberInput } from "@/components/ui/formatted-number-input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState, useCallback, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Package, Box, Layers, AlertTriangle, Loader2, Plus, Trash2 } from "lucide-react";
import type { Load, LoadItem } from "@shared/schema";

const HANDLING_UNITS = ["Pallet", "Crate", "Box", "Other"];

interface FreightItem {
  id: string;
  handlingUnit: string;
  length: string;
  width: string;
  height: string;
  weight: string;
  quantity: string;
  stackable: boolean;
  hazardous: boolean;
}

function createEmptyItem(): FreightItem {
  return {
    id: crypto.randomUUID(),
    handlingUnit: "Pallet",
    length: "",
    width: "",
    height: "",
    weight: "",
    quantity: "1",
    stackable: false,
    hazardous: false,
  };
}

const LOCATION_TYPES = [
  { value: "Business", label: "Business (with loading dock or forklift)" },
  { value: "BusinessNoDoc", label: "Business (without loading dock)" },
  { value: "Residential", label: "Residential" },
  { value: "Construction", label: "Construction Site" },
  { value: "TradeShow", label: "Trade Show/Convention" },
  { value: "Storage", label: "Storage Facility" },
];

const US_STATES = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
];

async function lookupZipCode(zip: string): Promise<{ city: string; state: string } | null> {
  if (!/^\d{5}$/.test(zip)) return null;
  try {
    const response = await fetch(`https://api.zippopotam.us/us/${zip}`);
    if (!response.ok) return null;
    const data = await response.json();
    if (data.places && data.places.length > 0) {
      return {
        city: data.places[0]["place name"],
        state: data.places[0]["state abbreviation"],
      };
    }
    return null;
  } catch {
    return null;
  }
}

function parseOriginDestination(location: string): { zip: string; city: string; state: string } {
  const match = location.match(/^(.+),\s*([A-Z]{2})\s*(\d{5})$/);
  if (match) {
    return { city: match[1].trim(), state: match[2], zip: match[3] };
  }
  const match2 = location.match(/^(.+),\s*([A-Z]{2})$/);
  if (match2) {
    return { city: match2[1].trim(), state: match2[2], zip: "" };
  }
  if (/^\d{5}$/.test(location.trim())) {
    return { city: "", state: "", zip: location.trim() };
  }
  return { city: location, state: "", zip: "" };
}

interface EditLoadModalProps {
  load: (Load & { items?: LoadItem[] }) | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export default function EditLoadModal({ load, open, onOpenChange, onSuccess }: EditLoadModalProps) {
  const { toast } = useToast();
  const [pickupZipLoading, setPickupZipLoading] = useState(false);
  const [deliveryZipLoading, setDeliveryZipLoading] = useState(false);
  const [freightItems, setFreightItems] = useState<FreightItem[]>([createEmptyItem()]);
  const [formData, setFormData] = useState({
    pickupZip: "",
    pickupCity: "",
    pickupState: "",
    deliveryZip: "",
    deliveryCity: "",
    deliveryState: "",
    pickupDate: "",
    deliveryDate: "",
    details: "",
    pickupLocationType: "Business",
    deliveryLocationType: "Business",
    liftgatePickup: false,
    insidePickup: false,
    liftgateDelivery: false,
    insideDelivery: false,
    deliveryAppointmentRequired: false,
    callBeforeDelivery: false,
  });

  useEffect(() => {
    if (load && open) {
      const pickup = parseOriginDestination(load.origin);
      const delivery = parseOriginDestination(load.destination);

      const pickupDateStr = load.pickupDate ? new Date(load.pickupDate).toISOString().split('T')[0] : "";
      const deliveryDateStr = load.deliveryDate ? new Date(load.deliveryDate).toISOString().split('T')[0] : "";

      setFormData({
        pickupZip: pickup.zip,
        pickupCity: pickup.city,
        pickupState: pickup.state,
        deliveryZip: delivery.zip,
        deliveryCity: delivery.city,
        deliveryState: delivery.state,
        pickupDate: pickupDateStr,
        deliveryDate: deliveryDateStr,
        details: load.details || "",
        pickupLocationType: load.pickupLocationType || "Business",
        deliveryLocationType: load.deliveryLocationType || "Business",
        liftgatePickup: load.liftgatePickup || false,
        insidePickup: load.insidePickup || false,
        liftgateDelivery: load.liftgateDelivery || false,
        insideDelivery: load.insideDelivery || false,
        deliveryAppointmentRequired: load.deliveryAppointmentRequired || false,
        callBeforeDelivery: load.callBeforeDelivery || false,
      });

      if (load.items && load.items.length > 0) {
        setFreightItems(load.items.map(item => ({
          id: crypto.randomUUID(),
          handlingUnit: item.handlingUnit || "Pallet",
          length: item.length?.toString() || "",
          width: item.width?.toString() || "",
          height: item.height?.toString() || "",
          weight: item.weight?.toString() || "",
          quantity: item.quantity?.toString() || "1",
          stackable: item.stackable || false,
          hazardous: item.hazardous || false,
        })));
      } else {
        setFreightItems([{
          id: crypto.randomUUID(),
          handlingUnit: load.handlingUnit || "Pallet",
          length: load.length?.toString() || "",
          width: load.width?.toString() || "",
          height: load.height?.toString() || "",
          weight: load.weight?.toString() || "",
          quantity: load.quantity?.toString() || "1",
          stackable: load.stackable || false,
          hazardous: load.hazardous || false,
        }]);
      }
    }
  }, [load, open]);

  const updateFreightItem = useCallback((id: string, updates: Partial<FreightItem>) => {
    setFreightItems(prev => prev.map(item =>
      item.id === id ? { ...item, ...updates } : item
    ));
  }, []);

  const addFreightItem = useCallback(() => {
    setFreightItems(prev => [...prev, createEmptyItem()]);
  }, []);

  const removeFreightItem = useCallback((id: string) => {
    setFreightItems(prev => prev.length > 1 ? prev.filter(item => item.id !== id) : prev);
  }, []);

  const handlePickupZipChange = useCallback(async (zip: string) => {
    setFormData(prev => ({ ...prev, pickupZip: zip }));
    if (zip.length === 5) {
      setPickupZipLoading(true);
      const result = await lookupZipCode(zip);
      setPickupZipLoading(false);
      if (result) {
        setFormData(prev => ({ ...prev, pickupCity: result.city, pickupState: result.state }));
      }
    }
  }, []);

  const handleDeliveryZipChange = useCallback(async (zip: string) => {
    setFormData(prev => ({ ...prev, deliveryZip: zip }));
    if (zip.length === 5) {
      setDeliveryZipLoading(true);
      const result = await lookupZipCode(zip);
      setDeliveryZipLoading(false);
      if (result) {
        setFormData(prev => ({ ...prev, deliveryCity: result.city, deliveryState: result.state }));
      }
    }
  }, []);

  const totalWeight = freightItems.reduce((sum, item) => {
    const weight = parseInt(item.weight) || 0;
    const quantity = parseInt(item.quantity) || 1;
    return sum + (weight * quantity);
  }, 0);

  const mutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await apiRequest("PATCH", `/api/loads/${load?.id}`, data);
      return response.json();
    },
    onSuccess: () => {
      toast({ title: "Load Updated", description: "Your load has been updated successfully." });
      queryClient.invalidateQueries({ queryKey: ["/api/loads"] });
      onSuccess?.();
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to update load", variant: "destructive" });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const origin = formData.pickupCity && formData.pickupState
      ? `${formData.pickupCity}, ${formData.pickupState} ${formData.pickupZip}`
      : formData.pickupZip;

    const destination = formData.deliveryCity && formData.deliveryState
      ? `${formData.deliveryCity}, ${formData.deliveryState} ${formData.deliveryZip}`
      : formData.deliveryZip;

    const firstItem = freightItems[0];
    const dimensions = firstItem.length && firstItem.width && firstItem.height
      ? `${firstItem.length} x ${firstItem.width} x ${firstItem.height}`
      : "Standard";

    const items = freightItems.map(item => ({
      handlingUnit: item.handlingUnit,
      length: item.length ? parseInt(item.length) : null,
      width: item.width ? parseInt(item.width) : null,
      height: item.height ? parseInt(item.height) : null,
      weight: parseInt(item.weight) || 0,
      quantity: parseInt(item.quantity) || 1,
      stackable: item.stackable,
      hazardous: item.hazardous,
    }));

    mutation.mutate({
      origin,
      destination,
      weight: totalWeight,
      dimensions,
      pickupDate: new Date(formData.pickupDate).toISOString(),
      deliveryDate: formData.deliveryDate ? new Date(formData.deliveryDate).toISOString() : null,
      details: formData.details,
      handlingUnit: firstItem.handlingUnit,
      length: firstItem.length ? parseInt(firstItem.length) : null,
      width: firstItem.width ? parseInt(firstItem.width) : null,
      height: firstItem.height ? parseInt(firstItem.height) : null,
      quantity: parseInt(firstItem.quantity) || 1,
      stackable: firstItem.stackable,
      hazardous: firstItem.hazardous,
      pickupLocationType: formData.pickupLocationType,
      deliveryLocationType: formData.deliveryLocationType,
      liftgatePickup: formData.liftgatePickup,
      insidePickup: formData.insidePickup,
      liftgateDelivery: formData.liftgateDelivery,
      insideDelivery: formData.insideDelivery,
      deliveryAppointmentRequired: formData.deliveryAppointmentRequired,
      callBeforeDelivery: formData.callBeforeDelivery,
      items,
    });
  };

  if (!load) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid="edit-load-modal-title">Edit Load</DialogTitle>
          <DialogDescription>
            Update the details of load {load.loadIdentifier || load.id}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Card className="p-4">
            <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Package className="w-5 h-5" />
                Freight Information
              </h3>
              {totalWeight > 0 && (
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Total Weight:</p>
                  <p className="text-lg font-bold" data-testid="text-edit-total-weight">{totalWeight.toLocaleString()} lbs</p>
                </div>
              )}
            </div>

            {freightItems.map((item, index) => (
              <div
                key={item.id}
                className={`${index > 0 ? 'mt-6 pt-6 border-t' : ''}`}
                data-testid={`edit-freight-item-${index}`}
              >
                <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
                  <span className="text-sm font-medium text-muted-foreground">ITEM {index + 1}</span>
                  {freightItems.length > 1 && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeFreightItem(item.id)} className="text-destructive hover:text-destructive" data-testid={`button-edit-remove-item-${index}`}>
                      <Trash2 className="w-4 h-4 mr-1" />
                      Remove
                    </Button>
                  )}
                </div>

                <div className="mb-4">
                  <Label className="text-sm font-medium text-muted-foreground mb-2 block">HANDLING UNIT</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {HANDLING_UNITS.map((unit) => (
                      <Button key={unit} type="button" variant={item.handlingUnit === unit ? "default" : "outline"} onClick={() => updateFreightItem(item.id, { handlingUnit: unit })} className="flex items-center gap-2 justify-start" data-testid={`button-edit-unit-${unit.toLowerCase()}-${index}`}>
                        {unit === "Pallet" && <Layers className="w-4 h-4" />}
                        {unit === "Crate" && <Box className="w-4 h-4" />}
                        {unit === "Box" && <Package className="w-4 h-4" />}
                        {unit === "Other" && <Package className="w-4 h-4" />}
                        <span className="font-medium">{unit}</span>
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
                  <div className="space-y-1">
                    <Label className="text-xs">LENGTH (in)</Label>
                    <FormattedNumberInput decimals={0} placeholder="48" value={item.length} onChange={(val) => updateFreightItem(item.id, { length: val })} data-testid={`input-edit-length-${index}`} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">WIDTH (in)</Label>
                    <FormattedNumberInput decimals={0} placeholder="40" value={item.width} onChange={(val) => updateFreightItem(item.id, { width: val })} data-testid={`input-edit-width-${index}`} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">HEIGHT (in)</Label>
                    <FormattedNumberInput decimals={0} placeholder="62" value={item.height} onChange={(val) => updateFreightItem(item.id, { height: val })} data-testid={`input-edit-height-${index}`} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">WEIGHT (lbs) *</Label>
                    <FormattedNumberInput decimals={0} placeholder="277" value={item.weight} onChange={(val) => updateFreightItem(item.id, { weight: val })} data-testid={`input-edit-weight-${index}`} required={index === 0} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">QUANTITY</Label>
                    <FormattedNumberInput decimals={0} placeholder="1" value={item.quantity} onChange={(val) => updateFreightItem(item.id, { quantity: val })} data-testid={`input-edit-quantity-${index}`} />
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-6">
                  <div className="flex items-center gap-2">
                    <Checkbox id={`edit-stackable-${item.id}`} checked={item.stackable} onCheckedChange={(checked) => updateFreightItem(item.id, { stackable: checked === true })} data-testid={`checkbox-edit-stackable-${index}`} />
                    <Label htmlFor={`edit-stackable-${item.id}`} className="text-sm">Stackable</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox id={`edit-hazardous-${item.id}`} checked={item.hazardous} onCheckedChange={(checked) => updateFreightItem(item.id, { hazardous: checked === true })} data-testid={`checkbox-edit-hazardous-${index}`} />
                    <Label htmlFor={`edit-hazardous-${item.id}`} className="text-sm flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3 text-yellow-500" />
                      Hazardous
                    </Label>
                  </div>
                </div>
              </div>
            ))}

            <div className="mt-6 pt-4 border-t">
              <Button type="button" variant="outline" onClick={addFreightItem} className="w-full" data-testid="button-edit-add-item">
                <Plus className="w-4 h-4 mr-2" />
                Add Another Item
              </Button>
            </div>
          </Card>

          <Card className="p-4">
            <h3 className="text-lg font-semibold mb-4">Pickup Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="space-y-2">
                <Label>PICKUP DATE *</Label>
                <Input type="date" value={formData.pickupDate} onChange={(e) => setFormData({ ...formData, pickupDate: e.target.value })} data-testid="input-edit-pickup-date" required />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div className="space-y-2">
                <Label>ZIP CODE *</Label>
                <div className="relative">
                  <Input placeholder="30107" maxLength={5} value={formData.pickupZip} onChange={(e) => handlePickupZipChange(e.target.value.replace(/\D/g, ''))} data-testid="input-edit-pickup-zip" required />
                  {pickupZipLoading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />}
                </div>
              </div>
              <div className="space-y-2">
                <Label>CITY *</Label>
                <Input placeholder="Atlanta" value={formData.pickupCity} onChange={(e) => setFormData({ ...formData, pickupCity: e.target.value })} data-testid="input-edit-pickup-city" required />
              </div>
              <div className="space-y-2">
                <Label>STATE *</Label>
                <Select value={formData.pickupState} onValueChange={(value) => setFormData({ ...formData, pickupState: value })}>
                  <SelectTrigger data-testid="select-edit-pickup-state"><SelectValue placeholder="Select state" /></SelectTrigger>
                  <SelectContent>{US_STATES.map((state) => (<SelectItem key={state.value} value={state.value}>{state.label}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>LOCATION TYPE</Label>
                <Select value={formData.pickupLocationType} onValueChange={(value) => setFormData({ ...formData, pickupLocationType: value })}>
                  <SelectTrigger data-testid="select-edit-pickup-location-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{LOCATION_TYPES.map((type) => (<SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>))}</SelectContent>
                </Select>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <h3 className="text-lg font-semibold mb-4">Delivery Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="space-y-2">
                <Label>DELIVERY DATE</Label>
                <Input type="date" value={formData.deliveryDate} onChange={(e) => setFormData({ ...formData, deliveryDate: e.target.value })} data-testid="input-edit-delivery-date" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div className="space-y-2">
                <Label>ZIP CODE *</Label>
                <div className="relative">
                  <Input placeholder="84104" maxLength={5} value={formData.deliveryZip} onChange={(e) => handleDeliveryZipChange(e.target.value.replace(/\D/g, ''))} data-testid="input-edit-delivery-zip" required />
                  {deliveryZipLoading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />}
                </div>
              </div>
              <div className="space-y-2">
                <Label>CITY *</Label>
                <Input placeholder="Salt Lake City" value={formData.deliveryCity} onChange={(e) => setFormData({ ...formData, deliveryCity: e.target.value })} data-testid="input-edit-delivery-city" required />
              </div>
              <div className="space-y-2">
                <Label>STATE *</Label>
                <Select value={formData.deliveryState} onValueChange={(value) => setFormData({ ...formData, deliveryState: value })}>
                  <SelectTrigger data-testid="select-edit-delivery-state"><SelectValue placeholder="Select state" /></SelectTrigger>
                  <SelectContent>{US_STATES.map((state) => (<SelectItem key={state.value} value={state.value}>{state.label}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>LOCATION TYPE</Label>
                <Select value={formData.deliveryLocationType} onValueChange={(value) => setFormData({ ...formData, deliveryLocationType: value })}>
                  <SelectTrigger data-testid="select-edit-delivery-location-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{LOCATION_TYPES.map((type) => (<SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>))}</SelectContent>
                </Select>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <h3 className="text-lg font-semibold mb-2">Shipping Services</h3>
            <p className="text-sm text-muted-foreground mb-4">Select all services needed for this shipment.</p>
            <div className="mb-4">
              <Label className="text-sm font-medium text-muted-foreground mb-3 block">PICKUP SERVICES</Label>
              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-2">
                  <Checkbox id="edit-liftgatePickup" checked={formData.liftgatePickup} onCheckedChange={(checked) => setFormData({ ...formData, liftgatePickup: checked === true })} data-testid="checkbox-edit-liftgate-pickup" />
                  <Label htmlFor="edit-liftgatePickup">Liftgate Service at Pickup</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox id="edit-insidePickup" checked={formData.insidePickup} onCheckedChange={(checked) => setFormData({ ...formData, insidePickup: checked === true })} data-testid="checkbox-edit-inside-pickup" />
                  <Label htmlFor="edit-insidePickup">Inside Pickup</Label>
                </div>
              </div>
            </div>
            <div className="mb-4">
              <Label className="text-sm font-medium text-muted-foreground mb-3 block">DELIVERY SERVICES</Label>
              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-2">
                  <Checkbox id="edit-liftgateDelivery" checked={formData.liftgateDelivery} onCheckedChange={(checked) => setFormData({ ...formData, liftgateDelivery: checked === true })} data-testid="checkbox-edit-liftgate-delivery" />
                  <Label htmlFor="edit-liftgateDelivery">Liftgate Service at Delivery</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox id="edit-insideDelivery" checked={formData.insideDelivery} onCheckedChange={(checked) => setFormData({ ...formData, insideDelivery: checked === true })} data-testid="checkbox-edit-inside-delivery" />
                  <Label htmlFor="edit-insideDelivery">Inside Delivery</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox id="edit-deliveryAppointmentRequired" checked={formData.deliveryAppointmentRequired} onCheckedChange={(checked) => setFormData({ ...formData, deliveryAppointmentRequired: checked === true })} data-testid="checkbox-edit-delivery-appointment" />
                  <Label htmlFor="edit-deliveryAppointmentRequired">Delivery Appt. Required</Label>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="edit-callBeforeDelivery" checked={formData.callBeforeDelivery} onCheckedChange={(checked) => setFormData({ ...formData, callBeforeDelivery: checked === true })} data-testid="checkbox-edit-call-before-delivery" />
              <Label htmlFor="edit-callBeforeDelivery">Call before Delivery</Label>
            </div>
          </Card>

          <Card className="p-4">
            <h3 className="text-lg font-semibold mb-4">Details (Optional)</h3>
            <div className="space-y-2">
              <Label>Additional Notes</Label>
              <Textarea placeholder="Any special handling instructions..." value={formData.details} onChange={(e) => setFormData({ ...formData, details: e.target.value })} data-testid="textarea-edit-description" rows={3} />
            </div>
          </Card>

          <div className="flex gap-4">
            <Button type="submit" className="flex-1" disabled={mutation.isPending} data-testid="button-save-load">
              {mutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="button-cancel-edit-load">
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
